import React, { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash/debounce';
import Rails from 'rails-ujs';

const UserDictionaryPreview = () => {
  const [fromWord, setFromWord] = useState('');
  const [toWord, setToWord] = useState('');
  const [previewProducts, setPreviewProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // デバウンスされたAPI呼び出し
  const fetchPreview = useCallback(
    debounce((from, to) => {
      if (!from) {
        setPreviewProducts([]);
        return;
      }
      
      setLoading(true);
      
      // 現在のホスト（サブドメイン）からAPIのベースパスを判断
      const hostname = window.location.hostname;
      let apiPath = '/user_dictionaries/preview';

      // サブドメインベースでルーティングされているため、パスに/qoo10や/rakutenは含まれない
      // そのまま/user_dictionaries/previewを使用
      
      const params = new URLSearchParams({
        from_word: from,
        to_word: to || ''
      });
      
      Rails.ajax({
        type: 'GET',
        url: `${apiPath}?${params}`,
        success: (data) => {
          setPreviewProducts(data);
          setLoading(false);
        },
        error: () => {
          setPreviewProducts([]);
          setLoading(false);
        }
      });
    }, 500),
    []
  );
  
  useEffect(() => {
    // フォームの入力フィールドを監視
    const fromInput = document.getElementById('user_dictionary_from_word');
    const toInput = document.getElementById('user_dictionary_to_word');
    
    if (fromInput && toInput) {
      const handleInput = () => {
        const from = fromInput.value;
        const to = toInput.value;
        setFromWord(from);
        setToWord(to);
        fetchPreview(from, to);
      };
      
      fromInput.addEventListener('input', handleInput);
      toInput.addEventListener('input', handleInput);
      
      // 初期値を設定（編集画面の場合）
      if (fromInput.value || toInput.value) {
        handleInput();
      }
      
      return () => {
        fromInput.removeEventListener('input', handleInput);
        toInput.removeEventListener('input', handleInput);
      };
    }
  }, [fetchPreview]);
  
  const highlightChanges = (original, replaced) => {
    if (!fromWord || !original) {
      return original || '';
    }
    
    // toWordが空文字の場合は削除を示す
    if (!toWord && original === replaced) {
      return original;
    }
    
    // 置換箇所を探してハイライト
    const escapeRegExp = (string) => {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };
    
    // 元のテキストと置換後のテキストを比較
    let result = [];
    let lastIndex = 0;
    const regex = new RegExp(escapeRegExp(fromWord), 'gi');
    let match;
    
    const originalText = original || '';
    while ((match = regex.exec(originalText)) !== null) {
      // マッチ前のテキスト
      if (match.index > lastIndex) {
        result.push(
          <span key={`text-${lastIndex}`}>
            {originalText.substring(lastIndex, match.index)}
          </span>
        );
      }
      // マッチした部分を置換後の文字でハイライト（空文字の場合は削除を示す）
      if (toWord) {
        result.push(
          <span key={`highlight-${match.index}`} className="bg-warning text-dark px-1">
            {toWord}
          </span>
        );
      } else {
        // 空文字の場合は削除を赤い打ち消し線で表示
        result.push(
          <span key={`highlight-${match.index}`} className="text-danger text-decoration-line-through">
            {match[0]}
          </span>
        );
      }
      lastIndex = match.index + match[0].length;
    }
    
    // 残りのテキスト
    if (lastIndex < originalText.length) {
      result.push(
        <span key={`text-${lastIndex}`}>
          {originalText.substring(lastIndex)}
        </span>
      );
    }
    
    return result.length > 0 ? result : originalText;
  };
  
  return (
    <div className="card mt-4">
      <div className="card-header">
        <h5 className="mb-0">
          置換プレビュー
          {loading && <span className="spinner-border spinner-border-sm ms-2" />}
        </h5>
      </div>
      <div className="card-body">
        {!fromWord && (
          <div className="alert alert-secondary">
            置換前の単語を入力すると、該当する商品が表示されます
          </div>
        )}
        
        {fromWord && (
          <div className="alert alert-info">
            <strong>「{fromWord}」</strong> → <strong className="text-warning">「{toWord || '(削除)'}」</strong> に置換（大文字小文字を無視）
          </div>
        )}
        
        {fromWord && !loading && previewProducts.length === 0 && (
          <div className="alert alert-warning">
            「{fromWord}」を含む商品が見つかりませんでした
          </div>
        )}
        
        {previewProducts.length > 0 && (
          <div className="table-responsive">
            <table className="table table-sm table-bordered">
              <thead className="table-light">
                <tr>
                  <th style={{width: '100px'}}>ASIN</th>
                  <th style={{width: '120px'}}>項目</th>
                  <th style={{width: '45%'}}>置換前</th>
                  <th style={{width: '45%'}}>置換後</th>
                </tr>
              </thead>
              <tbody>
              {previewProducts.map(product => (
                <React.Fragment key={product.id}>
                  <tr>
                    <td rowSpan="2" className="align-middle text-center">
                      <small>{product.asin}</small>
                    </td>
                    <td className="align-middle">
                      <strong>商品名</strong>
                    </td>
                    <td>
                      {product.itemName || '（商品名なし）'}
                    </td>
                    <td className="bg-light">
                      {highlightChanges(product.itemName, product.replacedName)}
                    </td>
                  </tr>
                  <tr>
                    <td className="align-middle">
                      <strong>説明文</strong>
                    </td>
                    <td>
                      <div style={{maxHeight: '200px', overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                        {product.description || '（説明文なし）'}
                      </div>
                    </td>
                    <td className="bg-light">
                      <div style={{maxHeight: '200px', overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                        {highlightChanges(product.description, product.replacedDescription)}
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
              </tbody>
            </table>
          </div>
        )}
        
        {previewProducts.length > 0 && (
          <div className="text-muted small mt-2">
            {previewProducts.length}件の商品を表示中（最大10件）
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDictionaryPreview;